import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Represents a pinned document in the dock
 */
export interface PinnedDocument {
  /** Unique identifier (blob ID) */
  id: string;
  /** Associated matter ID for navigation */
  matterId: string;
  /** Display name of matter (for context) */
  matterName: string;
  /** Display name of the file */
  fileName: string;
  /** MIME type for preview rendering */
  fileType: string;
  /** URL to fetch document */
  downloadUrl: string;
  /** ISO timestamp when pinned */
  pinnedAt: string;
}

interface PinnedDocsState {
  /** List of pinned documents */
  pinnedDocs: PinnedDocument[];
  /** ID of document currently in mini view (null if collapsed) */
  expandedDocId: string | null;
  /** ID of document currently in fullscreen (null if not fullscreen) */
  fullscreenDocId: string | null;
  /** Whether the dock is visible */
  isDockVisible: boolean;
}

const STORAGE_KEY = 'pinned-docs-v1';
const MAX_PINNED_DOCS = 20;
const SOFT_LIMIT = 10;

// Load initial state from localStorage
const loadFromStorage = (): PinnedDocument[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return parsed.slice(0, MAX_PINNED_DOCS);
      }
    }
  } catch {
    // Silent fail - return empty array
  }
  return [];
};

// Save to localStorage
const saveToStorage = (docs: PinnedDocument[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(docs));
  } catch {
    // Silent fail - localStorage might be full
  }
};

const initialState: PinnedDocsState = {
  pinnedDocs: loadFromStorage(),
  expandedDocId: null,
  fullscreenDocId: null,
  isDockVisible: true,
};

export const pinnedDocsSlice = createSlice({
  name: 'pinnedDocs',
  initialState,
  reducers: {
    pinDocument: (state, action: PayloadAction<PinnedDocument>) => {
      // Check if already pinned
      const existing = state.pinnedDocs.find(
        (doc) => doc.id === action.payload.id,
      );
      if (existing) {
        return;
      }

      // Check hard limit
      if (state.pinnedDocs.length >= MAX_PINNED_DOCS) {
        return;
      }

      state.pinnedDocs.push(action.payload);
      saveToStorage(state.pinnedDocs);
    },

    unpinDocument: (state, action: PayloadAction<string>) => {
      const docId = action.payload;
      state.pinnedDocs = state.pinnedDocs.filter((doc) => doc.id !== docId);

      // Clear expanded/fullscreen if unpinning that doc
      if (state.expandedDocId === docId) {
        state.expandedDocId = null;
      }
      if (state.fullscreenDocId === docId) {
        state.fullscreenDocId = null;
      }

      saveToStorage(state.pinnedDocs);
    },

    expandDocument: (state, action: PayloadAction<string>) => {
      const docId = action.payload;
      // Close fullscreen if open
      state.fullscreenDocId = null;
      // Toggle expand (click same tab = collapse)
      state.expandedDocId = state.expandedDocId === docId ? null : docId;
    },

    collapseDocument: (state) => {
      state.expandedDocId = null;
      state.fullscreenDocId = null;
    },

    openFullscreen: (state, action: PayloadAction<string>) => {
      state.fullscreenDocId = action.payload;
      // Keep expanded state for when we close fullscreen
    },

    closeFullscreen: (state) => {
      state.fullscreenDocId = null;
      // Return to mini view (expanded state persists)
    },

    toggleDockVisibility: (state) => {
      state.isDockVisible = !state.isDockVisible;
    },

    reorderTabs: (
      state,
      action: PayloadAction<{ fromIndex: number; toIndex: number }>,
    ) => {
      const { fromIndex, toIndex } = action.payload;
      const [removed] = state.pinnedDocs.splice(fromIndex, 1);
      state.pinnedDocs.splice(toIndex, 0, removed);
      saveToStorage(state.pinnedDocs);
    },

    clearAllPinned: (state) => {
      state.pinnedDocs = [];
      state.expandedDocId = null;
      state.fullscreenDocId = null;
      saveToStorage(state.pinnedDocs);
    },
  },
});

export const {
  pinDocument,
  unpinDocument,
  expandDocument,
  collapseDocument,
  openFullscreen,
  closeFullscreen,
  toggleDockVisibility,
  reorderTabs,
  clearAllPinned,
} = pinnedDocsSlice.actions;

// Selectors
export const selectPinnedDocs = (state: { pinnedDocs: PinnedDocsState }) =>
  state.pinnedDocs.pinnedDocs;

export const selectExpandedDocId = (state: { pinnedDocs: PinnedDocsState }) =>
  state.pinnedDocs.expandedDocId;

export const selectFullscreenDocId = (state: { pinnedDocs: PinnedDocsState }) =>
  state.pinnedDocs.fullscreenDocId;

export const selectIsDockVisible = (state: { pinnedDocs: PinnedDocsState }) =>
  state.pinnedDocs.isDockVisible;

export const selectExpandedDoc = (state: { pinnedDocs: PinnedDocsState }) => {
  const expandedId = state.pinnedDocs.expandedDocId;
  if (!expandedId) return null;
  return (
    state.pinnedDocs.pinnedDocs.find((doc) => doc.id === expandedId) || null
  );
};

export const selectFullscreenDoc = (state: { pinnedDocs: PinnedDocsState }) => {
  const fullscreenId = state.pinnedDocs.fullscreenDocId;
  if (!fullscreenId) return null;
  return (
    state.pinnedDocs.pinnedDocs.find((doc) => doc.id === fullscreenId) || null
  );
};

export const selectIsAtSoftLimit = (state: { pinnedDocs: PinnedDocsState }) =>
  state.pinnedDocs.pinnedDocs.length >= SOFT_LIMIT;

export const selectIsAtHardLimit = (state: { pinnedDocs: PinnedDocsState }) =>
  state.pinnedDocs.pinnedDocs.length >= MAX_PINNED_DOCS;

export const selectIsDocPinned =
  (docId: string) => (state: { pinnedDocs: PinnedDocsState }) =>
    state.pinnedDocs.pinnedDocs.some((doc) => doc.id === docId);

export default pinnedDocsSlice.reducer;
